import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useEvent } from "expo"
import { Image } from "expo-image"
import { router, useFocusEffect, useLocalSearchParams } from "expo-router"
import { StatusBar } from "expo-status-bar"
import { useVideoPlayer, VideoView } from "expo-video"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import YoutubePlayer from "react-native-youtube-iframe"

import type { PdfRef } from "src/components/NativePdfView"
import { CompositeInputCluster } from "src/components/CompositeInputCluster"
import { LoginOrRequestAccessModal } from "src/components/ContentLibrary/LoginOrRequestAccessModal"
import { UnauthAvatarOverlay } from "src/components/ContentLibrary/UnauthAvatarOverlay"
import { Text } from "src/components/Ignite/Text"
import { MediaPlayerShell } from "src/components/MediaPlayerShell"
import { NativePdfView as Pdf } from "src/components/NativePdfView"
import { WebView } from "src/components/WebView"
import { AvatarMode, useAvatarMode } from "src/context/AvatarModeContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useWebViewNavigation } from "src/hooks/useWebViewNavigation"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { ThemedStyle } from "src/theme"
import { storage } from "src/utils/storage/mmkv"
import { getYouTubeVideoId } from "src/utils/youtubeUtils"

type MediaType = "video" | "pdf" | "audio" | "image" | "url" | "youtube" | "unknown"

/**
 * UnauthMediaViewerScreen — public (unauthenticated) media viewer for the
 * channel content library. Composes MediaPlayerShell so the visual chrome
 * matches the authenticated media players (video / audio / pdf / image /
 * web-browser). The bottom Talk/Type pill is a visual stand-in that opens a
 * login-or-request-access modal; it does not start a real conversation.
 */
export default function UnauthMediaViewerScreen() {
  const params = useLocalSearchParams<{
    channelId: string
    taskId?: string
    mediaType?: string
    url?: string
    title?: string
    thumbnailUrl?: string
  }>()

  const channelId = params.channelId ?? ""
  const taskId = params.taskId
  const mediaType = (params.mediaType ?? "unknown") as MediaType
  const url = params.url ?? ""
  const title = params.title ?? ""
  const thumbnailUrl = params.thumbnailUrl ?? ""

  const { themed, theme } = useAppTheme()
  const { colors } = theme
  const insets = useSafeAreaInsets()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const { isSpeaking, stopSpeech } = useVisemes()
  const { mode: avatarMode, setMode: setAvatarMode } = useAvatarMode()
  // Stable ref so the play/pause effect doesn't need `avatarMode` as a dep
  // (avoids re-running the effect on every avatar mode change).
  const avatarModeRef = useRef<AvatarMode>(avatarMode)
  avatarModeRef.current = avatarMode
  // The avatar mode to restore when playback pauses (defaults to minimized).
  const sizeBeforePlayRef = useRef<AvatarMode>("minimized")

  const { data: channelInfo } = useUnauthChannelInfoQuery(channelId)
  const subscriptionUrl = channelInfo?.subscription_url ?? null

  const [lockModalVisible, setLockModalVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // ── WebView navigation ─────────────────────────────────────────────────────
  const webViewRef = useRef<any>(null)
  const { canGoBack, canGoForward, goBack, goForward, webViewProps } =
    useWebViewNavigation(webViewRef)

  // ── PDF state ──────────────────────────────────────────────────────────────
  const pdfRef = useRef<PdfRef>(null)
  const [_numPages, setNumPages] = useState(0)
  const [pdfHasError, setPdfHasError] = useState(false)

  const handleBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
    } else {
      router.replace({
        pathname: "/unauth/channel-library/[id]",
        params: { id: channelId },
      } as any)
    }
  }, [channelId])

  const handleTalkTypePress = useCallback(() => {
    setLockModalVisible(true)
  }, [])

  const handleLoginPress = useCallback(() => {
    router.replace("/unauth/login")
  }, [])

  // Stop any in-progress welcome speech whenever the screen loses focus
  // (close button, login button, back gesture, or any other navigation).
  useFocusEffect(
    useCallback(() => {
      return () => {
        stopSpeech()
      }
    }, [stopSpeech]),
  )

  const isVideoType = mediaType === "video"
  const isAudioType = mediaType === "audio"
  const isImageType = mediaType === "image"
  const isPdfType = mediaType === "pdf"
  const isWebBased = mediaType === "url"
  const isYouTube = mediaType === "youtube"
  const youTubeVideoId = isYouTube ? (getYouTubeVideoId(url) ?? "") : ""
  // expo-video player is only used for non-YouTube video and audio
  const isPlayable = isVideoType || isAudioType

  // Compute a contained 16:9 player size so the YouTube iframe fills its container
  // without relying on YouTube's own letterboxing (which adds a black background).
  const youtubePlayerSize = useMemo(() => {
    const paddingHorizontal = 32
    const availableWidth = windowWidth - paddingHorizontal
    const availableHeight = windowHeight
    const byWidth = { width: availableWidth, height: availableWidth * (9 / 16) }
    const byHeight = { width: availableHeight * (16 / 9), height: availableHeight }
    return byWidth.height <= availableHeight ? byWidth : byHeight
  }, [windowWidth, windowHeight])

  const [isYouTubePlaying, setIsYouTubePlaying] = useState(false)

  // ── Player (only instantiated for video / audio) ───────────────────────────
  const player = useVideoPlayer(isPlayable ? url : "", (p) => {
    p.loop = false
    p.timeUpdateEventInterval = 0.1
    // Start paused; autoplay fires once the avatar intro finishes (or immediately
    // if there is no intro for this visit).
    p.pause()
  })

  // Playback state
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [, setScrubTime] = useState<number | null>(null)
  const [isWebFullscreen, setIsWebFullscreen] = useState(false)
  const pendingSeekRef = useRef<number | null>(null)

  const toggleWebFullscreen = useCallback(() => setIsWebFullscreen((v) => !v), [])

  const { isPlaying } = useEvent(player, "playingChange", { isPlaying: player.playing })
  const { status: playerStatus } = useEvent(player, "statusChange", { status: player.status })

  // Show the thumbnail_url poster over the video until it actually starts
  // playing, so the user never sees the raw first frame while it loads.
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  useEffect(() => {
    if (isPlaying) {
      setHasStartedPlaying(true)
    }
  }, [isPlaying])

  // ── Autoplay with intro-pitch gate ────────────────────────────────────────
  //
  // Problem: `isSpeaking` starts `false` at mount. If we simply gate on
  // `!isSpeaking`, the player fires before the async cm_subscription_benefits
  // fetch completes and the avatar starts speaking, causing video + speech
  // to overlap.
  //
  // Solution: on a first visit for this task, wait for speaking to have
  // started AND finished before autoplaying. A 5 s fallback covers cases
  // where the intro endpoint returns no data or the network is slow.
  const introBenefitsKey =
    channelId && taskId ? `unauth.cmBenefitsShown.${channelId}.${taskId}` : null
  // Capture at mount — before UnauthAvatarOverlay marks it shown.
  const isFirstVisitForTaskRef = useRef(
    !!introBenefitsKey && storage.getBoolean(introBenefitsKey) !== true,
  )

  // True while we are waiting for the intro to complete before autoplaying.
  const [waitingForIntro, setWaitingForIntro] = useState(isFirstVisitForTaskRef.current)
  const introStartedRef = useRef(false)
  const introFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Watch isSpeaking transitions to detect intro start → finish.
  useEffect(() => {
    if (!waitingForIntro) return
    if (isSpeaking) {
      introStartedRef.current = true
      if (introFallbackRef.current) {
        clearTimeout(introFallbackRef.current)
        introFallbackRef.current = null
      }
    } else if (introStartedRef.current) {
      setWaitingForIntro(false)
    }
  }, [isSpeaking, waitingForIntro])

  // Fallback: release the gate if the intro never starts within 5 s.
  useEffect(() => {
    if (!waitingForIntro) return
    introFallbackRef.current = setTimeout(() => setWaitingForIntro(false), 5000)
    return () => {
      if (introFallbackRef.current) {
        clearTimeout(introFallbackRef.current)
        introFallbackRef.current = null
      }
    }
  }, [waitingForIntro])

  const hasAutoplayed = useRef(false)
  useEffect(() => {
    if (!isPlayable || playerStatus !== "readyToPlay" || waitingForIntro || hasAutoplayed.current)
      return
    hasAutoplayed.current = true
    player.play()
  }, [isPlayable, playerStatus, waitingForIntro, player])
  const { duration: loadedDuration } = useEvent(player, "sourceLoad", {
    videoSource: null,
    duration: player.duration ?? 0,
    availableVideoTracks: [],
    availableSubtitleTracks: [],
    availableAudioTracks: [],
  })
  const { currentTime } = useEvent(player, "timeUpdate", {
    currentTime: player.currentTime ?? 0,
    currentLiveTimestamp: null,
    currentOffsetFromLive: null,
    bufferedPosition: player.bufferedPosition ?? 0,
  })

  // Sync duration from sourceLoad first, fall back to player.duration once ready.
  useEffect(() => {
    if (!isPlayable) return
    if (loadedDuration && Number.isFinite(loadedDuration) && loadedDuration > 0) {
      setDuration((current) =>
        Math.abs(current - loadedDuration) > 0.1 ? loadedDuration : current,
      )
      return
    }
    if (playerStatus === "readyToPlay") {
      const nativeDuration = player.duration ?? 0
      if (nativeDuration > 0 && Number.isFinite(nativeDuration)) {
        setDuration((current) =>
          Math.abs(current - nativeDuration) > 0.1 ? nativeDuration : current,
        )
      }
    }
  }, [isPlayable, loadedDuration, playerStatus, player])

  // Sync position from timeUpdate when not scrubbing.
  useEffect(() => {
    if (!isPlayable) return
    if (!isScrubbing && typeof currentTime === "number" && !Number.isNaN(currentTime)) {
      if (pendingSeekRef.current !== null) {
        if (Math.abs(currentTime - pendingSeekRef.current) < 0.5) {
          pendingSeekRef.current = null
        }
      }
      if (pendingSeekRef.current === null) {
        setPosition(currentTime)
      }
    }
  }, [isPlayable, currentTime, isScrubbing])

  // ── Controls opacity (fade close + scrubber + bottom pill while playing) ───
  const controlsOpacity = useRef(new Animated.Value(1)).current

  useEffect(() => {
    Animated.timing(controlsOpacity, {
      toValue: isPlayable && isPlaying ? 0 : 1,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [isPlaying, isPlayable, controlsOpacity])

  // ── Avatar mode: tab while playing, minimized while paused ──────────────────
  useEffect(() => {
    if (!isPlayable) return
    if (isPlaying) {
      if (avatarModeRef.current !== "tab") {
        sizeBeforePlayRef.current = avatarModeRef.current
      }
      setAvatarMode("tab", false)
    } else {
      // Only restore if we put the avatar into tab for playback.
      if (avatarModeRef.current === "tab") {
        setAvatarMode(sizeBeforePlayRef.current, false)
      }
    }
  }, [isPlaying, isPlayable, setAvatarMode])

  // ── Tap-to-toggle play/pause + center icon flash (only for playable) ───────
  const overlayOpacity = useRef(new Animated.Value(0)).current
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [overlayIcon, setOverlayIcon] = useState<"play-circle-filled" | "pause-circle">(
    "play-circle-filled",
  )
  const currentAnimationToken = useRef<object | null>(null)

  const flashOverlay = useCallback(
    (willPlay: boolean) => {
      const animationToken = {}
      currentAnimationToken.current = animationToken
      overlayOpacity.stopAnimation(() => {
        setOverlayIcon(willPlay ? "play-circle-filled" : "pause-circle")
        overlayOpacity.setValue(0.75)
        setIsOverlayVisible(true)
        Animated.timing(overlayOpacity, {
          toValue: 0,
          duration: 800,
          delay: 1500,
          useNativeDriver: true,
        }).start(({ finished }) => {
          if (finished && currentAnimationToken.current === animationToken) {
            setIsOverlayVisible(false)
          }
        })
      })
    },
    [overlayOpacity],
  )

  const togglePause = useCallback(() => {
    if (!isPlayable) return
    const willPlay = !isPlaying
    if (isPlaying) {
      player.pause()
    } else {
      // Interrupt avatar intro if it is still speaking, then play media.
      stopSpeech()
      hasAutoplayed.current = true
      player.play()
    }
    flashOverlay(willPlay)
  }, [isPlayable, isPlaying, player, stopSpeech, flashOverlay])

  // ── Content renderer ───────────────────────────────────────────────────────
  const renderContent = () => {
    if (isYouTube && youTubeVideoId) {
      return (
        <View style={styles.youtubeStage}>
          <View style={{ width: youtubePlayerSize.width, height: youtubePlayerSize.height }}>
            <YoutubePlayer
              height="100%"
              width="100%"
              videoId={youTubeVideoId}
              play={isYouTubePlaying}
              volume={50}
              playbackRate={1}
              onChangeState={(state: string) => {
                if (state === "playing") setIsYouTubePlaying(true)
                else if (state === "paused" || state === "ended") setIsYouTubePlaying(false)
              }}
              initialPlayerParams={{
                controls: true,
                cc_lang_pref: "us",
                iv_load_policy: 3,
                showClosedCaptions: false,
                preventFullScreen: false,
                rel: false,
                modestbranding: 1,
                showinfo: 0,
              }}
            />
          </View>
        </View>
      )
    }

    if (isVideoType && url) {
      return (
        <>
          <VideoView
            player={player}
            style={[
              styles.absoluteFill,
              Platform.OS === "web" &&
                ({
                  width: "100%",
                  height: "100%",
                } as unknown as ViewStyle),
            ]}
            contentFit="contain"
            nativeControls={false}
            allowsFullscreen
            allowsPictureInPicture={false}
            pointerEvents="none"
            {...(Platform.OS === "web" && { crossOrigin: "anonymous" as const })}
          />

          {/* Thumbnail poster shown over the video until playback begins, so
              the user sees thumbnail_url instead of the raw first frame. */}
          {!!thumbnailUrl && !hasStartedPlaying && (
            <Image
              source={{ uri: thumbnailUrl }}
              style={styles.absoluteFill}
              contentFit="contain"
              pointerEvents="none"
            />
          )}
        </>
      )
    }

    if (isAudioType && url) {
      // expo-video needs a media element to render; keep it 1×1 and offscreen.
      return (
        <>
          <VideoView
            player={player}
            style={styles.hiddenVideoView}
            nativeControls={false}
            contentFit="contain"
            allowsFullscreen={false}
            allowsPictureInPicture={false}
            pointerEvents="none"
            {...(Platform.OS === "web" && { crossOrigin: "anonymous" as const })}
          />
          <View style={styles.audioContainer} pointerEvents="none">
            <MaterialIcons name="volume-up" size={120} color={colors.text} />
            {!!title && (
              <Text style={[styles.audioTitle, { color: colors.text }]} numberOfLines={2}>
                {title}
              </Text>
            )}
          </View>
        </>
      )
    }

    if (isImageType && url) {
      return (
        <Image
          source={{ uri: url }}
          style={styles.absoluteFill}
          contentFit="contain"
          onLoad={() => setIsLoading(false)}
        />
      )
    }

    if (isWebBased && url) {
      return (
        <WebView
          ref={webViewRef}
          source={{ uri: url }}
          style={styles.absoluteFill}
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
          {...webViewProps}
        />
      )
    }

    if (isPdfType && url) {
      return (
        <View style={styles.pdfContainer}>
          {isLoading && (
            <View style={styles.pdfLoadingContainer}>
              <MaterialIcons name="hourglass-empty" size={32} color="#FFFFFF" />
              <Text style={styles.pdfLoadingText}>Loading document...</Text>
            </View>
          )}
          {pdfHasError && (
            <View style={styles.pdfErrorOverlay}>
              <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
              <Text style={styles.pdfErrorTitle}>Failed to Load PDF</Text>
              <Text style={styles.pdfErrorText}>
                The PDF could not be loaded. Please check your connection and try again.
              </Text>
            </View>
          )}
          <Pdf
            ref={pdfRef}
            source={{ uri: url, cache: true }}
            style={[styles.pdf, pdfHasError && styles.hidden]}
            trustAllCerts={false}
            onLoadComplete={(numberOfPages) => {
              setNumPages(numberOfPages)
              setIsLoading(false)
              setPdfHasError(false)
            }}
            onError={(error) => {
              console.warn("[UnauthMediaViewer] PDF load error:", error)
              setIsLoading(false)
              setPdfHasError(true)
            }}
            enablePaging={false}
            spacing={8}
          />
        </View>
      )
    }

    return (
      <View style={styles.unsupportedContainer}>
        <MaterialIcons name="error-outline" size={48} color={colors.textDim} />
        <Text style={themed($unsupportedText)}>This content type is not supported.</Text>
      </View>
    )
  }

  // ── Top-right accessory: login + close buttons grouped together ───────────
  const topRightButtons = (
    <View style={styles.topRightRow}>
      <TouchableOpacity
        onPress={handleLoginPress}
        style={[styles.compactLoginButton, { backgroundColor: colors.buttonBackground }]}
        accessibilityRole="button"
        accessibilityLabel="Log In"
      >
        <MaterialIcons name="login" size={18} color={colors.buttonText} />
        <Text style={[styles.compactLoginLabel, { color: colors.buttonText }]}>Log In</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={handleBack}
        style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
        accessibilityRole="button"
        accessibilityLabel="Close"
      >
        <MaterialIcons name="close" size={20} color={colors.buttonText} />
      </TouchableOpacity>
    </View>
  )

  // ── Bottom slot: cluster gated behind the account-required prompt ──────────
  // No style prop — bottomSlotFullWidth on the shell wrapper already provides
  // flex:1 width and alignItems:stretch. Passing flex:1 here would set
  // flexBasis:0 (Yoga shorthand), overriding the explicit height:56 and
  // collapsing the pill.
  const talkTypePill = <CompositeInputCluster onInteractionBlocked={handleTalkTypePress} />

  // ── Loading overlay (image / web only) ─────────────────────────────────────
  const showLoadingOverlay = isLoading && (isImageType || isWebBased) && !!url

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      <MediaPlayerShell
        darkBackground
        keepControlsOnKeyboard
        suppressBuiltInLeftControls
        showControlsGradient={false}
        controlsOpacity={controlsOpacity}
        controlsVisible={!(isPlayable && isPlaying)}
        onContentPress={isPlayable ? togglePause : undefined}
        topRightAccessory={topRightButtons}
        avatarSlot={
          <UnauthAvatarOverlay channelId={channelId} taskId={taskId} isMediaPlaying={isPlaying} />
        }
        controlsConfig={
          isPlayable
            ? {
                type: "playback",
                isPlaying,
                onTogglePlay: togglePause,
                position,
                duration,
                isScrubbing,
                onScrubStart: () => {
                  setIsScrubbing(true)
                  setScrubTime(position)
                },
                onScrubChange: (value) => {
                  setPosition(value)
                  setScrubTime(value)
                },
                onScrubEnd: (value) => {
                  setIsScrubbing(false)
                  setScrubTime(null)
                  setPosition(value)
                  pendingSeekRef.current = value
                  player.currentTime = value
                },
                ...(Platform.OS === "web" && {
                  isFullscreen: isWebFullscreen,
                  onToggleFullscreen: toggleWebFullscreen,
                }),
              }
            : isWebBased
              ? {
                  type: "navigation",
                  canGoBack,
                  canGoForward,
                  onGoBack: goBack,
                  onGoForward: goForward,
                }
              : undefined
        }
        bottomSlot={talkTypePill}
      >
        {renderContent()}
      </MediaPlayerShell>

      {/* Loading overlay for non-video content */}
      {showLoadingOverlay && (
        <View
          style={[styles.loadingOverlay, { paddingBottom: insets.bottom + 80 }]}
          pointerEvents="none"
        >
          <MaterialIcons name="hourglass-empty" size={48} color={colors.textDim} />
        </View>
      )}

      {/* Play / pause overlay animation (videos / audio) */}
      {isOverlayVisible && (
        <Animated.View
          pointerEvents="none"
          style={[styles.fullscreenOverlay, { opacity: overlayOpacity }]}
        >
          <MaterialIcons name={overlayIcon} size={120} color="#FFFFFF" accessibilityRole="image" />
        </Animated.View>
      )}

      {/* Login or Request Access modal */}
      <LoginOrRequestAccessModal
        visible={lockModalVisible}
        onDismiss={() => setLockModalVisible(false)}
        subscriptionUrl={subscriptionUrl}
        channelId={channelId}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  absoluteFill: {
    ...StyleSheet.absoluteFillObject,
  },
  hiddenVideoView: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    overflow: "hidden",
  },
  audioContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  audioTitle: {
    fontSize: 24,
    fontWeight: "600",
    textAlign: "center",
    marginTop: 32,
  },
  unsupportedContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: 32,
  },
  youtubeStage: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    backgroundColor: "#000000",
  },
  pdfContainer: {
    flex: 1,
    backgroundColor: "#000000",
  },
  pdf: {
    flex: 1,
    backgroundColor: "#000000",
  },
  hidden: {
    opacity: 0,
  },
  pdfLoadingContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    zIndex: 1,
  },
  pdfLoadingText: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 12,
  },
  pdfErrorOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    padding: 24,
    zIndex: 2,
  },
  pdfErrorTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
    marginTop: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  pdfErrorText: {
    fontSize: 14,
    color: "#FFFFFF",
    textAlign: "center",
  },
  topRightRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  compactLoginButton: {
    height: 36,
    borderRadius: 18,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    gap: 6,
  },
  compactLoginLabel: {
    fontSize: 14,
    fontWeight: "500",
  } as TextStyle,
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    zIndex: 1500,
  },
  fullscreenOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: 2050,
  },
})

const $unsupportedText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 14,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
  textAlign: "center",
})
